import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Filter,
  Image as ImageIcon,
  Pencil,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { toast } from '../../../ui/toast';
import {
  useDeleteCCAQuestionMutation,
  useGetStorageUrlQuery,
  useListCCAQuestionsQuery,
} from '../../../utils/api_service/ccaApi';
import { CCAQuestion } from '../../../utils/types/cca_types';
import {
  CCASubdomain,
  CCA_LEVEL_LABELS,
  CCA_SUBDOMAINS,
  PAGE_SIZE,
} from './ccaConstants';
import { CreateQuestionDialog } from './CreateQuestionDialog';

// ─── Delete confirmation dialog ──────────────────────────────────────────────

interface DeleteConfirmDialogProps {
  question: CCAQuestion;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteConfirmDialog = ({
  question,
  isDeleting,
  onConfirm,
  onCancel,
}: DeleteConfirmDialogProps) => (
  <Dialog
    open
    onClose={onCancel}
    maxWidth='xs'
    fullWidth
    PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
  >
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          bgcolor: '#fee2e2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#dc2626',
        }}
      >
        <Trash2 size={20} />
      </Box>
      <Box>
        <Typography variant='subtitle1' fontWeight={700}>
          Delete question?
        </Typography>
      </Box>
    </DialogTitle>
    <DialogContent sx={{ py: 1 }}>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
          overflow: 'hidden',
          fontStyle: 'italic',
        }}
      >
        &ldquo;{question.question.text}&rdquo;
      </Typography>
      <Typography variant='caption' color='error.main' sx={{ display: 'block', mt: 1 }}>
        This action cannot be undone.
      </Typography>
    </DialogContent>
    <DialogActions sx={{ px: 2, pb: 1.5 }}>
      <Button
        onClick={onCancel}
        disabled={isDeleting}
        variant='outlined'
        sx={{ textTransform: 'none', borderRadius: 2 }}
      >
        Cancel
      </Button>
      <Button
        onClick={onConfirm}
        disabled={isDeleting}
        variant='contained'
        color='error'
        sx={{ textTransform: 'none', borderRadius: 2 }}
      >
        {isDeleting ? 'Deleting…' : 'Delete'}
      </Button>
    </DialogActions>
  </Dialog>
);

const QUESTION_TYPE_LABELS: Record<string, string> = {
  multiple_choice: 'MC',
  short_answer: 'SA',
};

const subdomainColorMap: Record<string, { bg: string; color: string }> = {
  'Abstract Reasoning': { bg: '#ede9fe', color: '#6d28d9' },
  'Logical Reasoning': { bg: '#dbeafe', color: '#1d4ed8' },
  'Quantitative Aptitude': { bg: '#d1fae5', color: '#047857' },
  'Verbal Ability': { bg: '#fef3c7', color: '#b45309' },
  'Data Interpretation': { bg: '#ffe4e6', color: '#e11d48' },
};

const getSubdomainStyles = (subdomain: string) => {
  let mapped = subdomain;
  if (subdomain.includes('_')) {
    mapped = subdomain
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
  return subdomainColorMap[mapped] || { bg: '#f1f5f9', color: '#475569' };
};

const getLevelStyles = (level: number) => {
  if (level <= 2) return { bg: '#f1f5f9', color: '#475569' };
  if (level <= 4) return { bg: '#e0f2fe', color: '#0369a1' };
  if (level === 5) return { bg: '#dbeafe', color: '#1d4ed8' };
  if (level === 6) return { bg: '#e0e7ff', color: '#4338ca' };
  if (level === 7) return { bg: '#ede9fe', color: '#6d28d9' };
  if (level === 8) return { bg: '#f3e8ff', color: '#7e22ce' };
  if (level === 9) return { bg: '#fae8ff', color: '#a21caf' };
  return { bg: '#ffe4e6', color: '#be123c' };
};

/** Derive display format from whether the question has an imageKey */
const getFormat = (question: CCAQuestion): 'image' | 'wordy' =>
  question.question.imageKey ? 'image' : 'wordy';

const QuestionImage: React.FC<{ imageKey: string }> = ({ imageKey }) => {
  const { data, isLoading, isError } = useGetStorageUrlQuery(imageKey);

  if (isLoading) {
    return (
      <Paper
        variant='outlined'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <CircularProgress size={12} />
        <Typography variant='caption' color='text.secondary'>
          Loading…
        </Typography>
      </Paper>
    );
  }

  if (isError || !data?.url) {
    return (
      <Paper
        variant='outlined'
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 0.5,
          borderRadius: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <ImageIcon size={14} />
        <Typography variant='caption' color='text.secondary'>
          {imageKey.split('/').pop()}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      component='img'
      src={data.url}
      alt='Question image'
      sx={{
        maxHeight: 160,
        borderRadius: 2,
        objectFit: 'contain',
        border: '1px solid',
        borderColor: 'divider',
      }}
    />
  );
};

interface CCAQuestionRowProps {
  question: CCAQuestion;
  onEdit: (q: CCAQuestion) => void;
  onDelete: (q: CCAQuestion) => void;
}

const CCAQuestionRow = ({ question, onEdit, onDelete }: CCAQuestionRowProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const format = getFormat(question);
  const subdomainStyles = getSubdomainStyles(question.subdomain);
  const levelStyles = getLevelStyles(question.level);

  const displaySubdomain = question.subdomain.includes('_')
    ? question.subdomain
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
    : question.subdomain;

  return (
    <Paper
      variant='outlined'
      sx={{
        width: '100%',
        borderRadius: 2,
        borderColor: 'divider',
        bgcolor: '#ffffff',
        mb: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          cursor: 'pointer',
        }}
        onClick={() => setIsOpen((p) => !p)}
      >
        <IconButton
          size='small'
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen((p) => !p);
          }}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
          sx={{ color: 'text.secondary', p: 0.5 }}
        >
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </IconButton>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant='body2'
            fontWeight={600}
            color='text.primary'
            noWrap
          >
            {question.question.text}
          </Typography>
          <Stack
            direction='row'
            spacing={0.75}
            alignItems='center'
            flexWrap='wrap'
            sx={{ mt: 0.5 }}
          >
            <Chip
              label={displaySubdomain}
              size='small'
              sx={{
                bgcolor: subdomainStyles.bg,
                color: subdomainStyles.color,
                fontWeight: 600,
                fontSize: '0.6875rem',
                height: 20,
              }}
            />
            <Chip
              label={`Lvl ${question.level} — ${
                CCA_LEVEL_LABELS[question.level] ?? 'Unknown'
              }`}
              size='small'
              sx={{
                bgcolor: levelStyles.bg,
                color: levelStyles.color,
                fontWeight: 600,
                fontSize: '0.6875rem',
                height: 20,
              }}
            />
            <Chip
              label={
                QUESTION_TYPE_LABELS[question.questionType] ??
                question.questionType
              }
              size='small'
              sx={{
                bgcolor: '#e0e7ff',
                color: '#4338ca',
                fontWeight: 600,
                fontSize: '0.6875rem',
                height: 20,
              }}
            />
            <Chip
              label={format}
              size='small'
              sx={{
                bgcolor: 'grey.100',
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'capitalize',
                fontSize: '0.6875rem',
                height: 20,
              }}
            />
          </Stack>
        </Box>

        <Stack
          direction='row'
          spacing={0.5}
          alignItems='center'
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            size='small'
            onClick={() => onEdit(question)}
            aria-label='Edit question'
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: 'primary.light' },
            }}
          >
            <Pencil size={15} />
          </IconButton>
          <IconButton
            size='small'
            color='error'
            onClick={() => onDelete(question)}
            aria-label='Delete question'
          >
            <Trash2 size={15} />
          </IconButton>
        </Stack>
      </Box>

      {isOpen && (
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            px: 2.5,
            py: 2,
          }}
        >
          <Stack spacing={2}>
            {question.question.imageKey && (
              <Box>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  textTransform='uppercase'
                  color='text.secondary'
                  display='block'
                  sx={{ mb: 0.5 }}
                >
                  Question Image
                </Typography>
                <QuestionImage imageKey={question.question.imageKey} />
              </Box>
            )}

            {question.idealAnswer && (
              <Box>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  textTransform='uppercase'
                  color='text.secondary'
                  display='block'
                >
                  Ideal Answer
                </Typography>
                <Typography
                  variant='body2'
                  color='text.primary'
                  sx={{ mt: 0.25 }}
                >
                  {question.idealAnswer}
                </Typography>
              </Box>
            )}

            {(question.questionType === 'multiple-choice' ||
              question.questionType === 'multiple_choice') &&
              question.options.length > 0 && (
                <Box>
                  <Typography
                    variant='caption'
                    fontWeight={700}
                    textTransform='uppercase'
                    color='text.secondary'
                    display='block'
                    sx={{ mb: 1 }}
                  >
                    Choices
                  </Typography>
                  <Stack spacing={1}>
                    {question.options.map((opt) => (
                      <Paper
                        key={opt.id}
                        variant='outlined'
                        sx={{
                          p: 1.25,
                          borderRadius: 2,
                          bgcolor: opt.isCorrect ? '#ecfdf5' : '#f8fafc',
                          borderColor: opt.isCorrect ? '#a7f3d0' : 'divider',
                        }}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          {opt.isCorrect && (
                            <Check size={16} color='#059669' />
                          )}
                          <Typography
                            variant='caption'
                            fontWeight={opt.isCorrect ? 600 : 500}
                            color={opt.isCorrect ? '#065f46' : 'text.primary'}
                          >
                            {opt.text}
                          </Typography>
                        </Stack>
                        {opt.imageKey && (
                          <Box sx={{ mt: 1 }}>
                            <QuestionImage imageKey={opt.imageKey} />
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export const CCABank: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [selectedSubdomain, setSelectedSubdomain] = useState<
    CCASubdomain | 'All'
  >('All');
  const [selectedLevel, setSelectedLevel] = useState<number | 'All'>('All');
  const [selectedType, setSelectedType] = useState<string | 'All'>('All');
  const [page, setPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<CCAQuestion | null>(
    null,
  );
  const [deletingQuestion, setDeletingQuestion] = useState<CCAQuestion | null>(
    null,
  );

  const [deleteQuestion, { isLoading: isDeleting }] =
    useDeleteCCAQuestionMutation();

  const handleEdit = (q: CCAQuestion) => {
    setEditingQuestion(q);
    setIsCreateOpen(true);
  };

  const handleDelete = (q: CCAQuestion) => {
    setDeletingQuestion(q);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingQuestion) return;
    try {
      await deleteQuestion(deletingQuestion.id).unwrap();
      toast.success('Question deleted.', { position: 'bottom-center' });
      setDeletingQuestion(null);
    } catch {
      toast.error('Failed to delete question.', { position: 'bottom-center' });
    }
  };

  const handleDialogClose = () => {
    setIsCreateOpen(false);
    setEditingQuestion(null);
  };

  const formatSubdomain = (sd: CCASubdomain | 'All'): string | undefined => {
    if (sd === 'All') return undefined;
    return sd.toLowerCase().replace(/ /g, '_');
  };

  const formatQuestionType = (qt: string | 'All'): string | undefined => {
    if (qt === 'All') return undefined;
    return qt.replace(/-/g, '_');
  };

  // Server-side filtering via API params
  const { data, isLoading, isError } = useListCCAQuestionsQuery({
    subdomain: formatSubdomain(selectedSubdomain),
    level: selectedLevel !== 'All' ? selectedLevel : undefined,
    question_type: formatQuestionType(selectedType),
    page,
    page_size: PAGE_SIZE,
  });

  // Client-side search on the current page's results
  const visibleQuestions = useMemo(() => {
    if (!data?.items) return [];
    if (!searchText.trim()) return data.items;
    const lower = searchText.toLowerCase();
    return data.items.filter((q) =>
      q.question.text.toLowerCase().includes(lower),
    );
  }, [data, searchText]);

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const hasFilters =
    searchText ||
    selectedSubdomain !== 'All' ||
    selectedLevel !== 'All' ||
    selectedType !== 'All';

  const handleFilterChange = () => setPage(1);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2.5,
              bgcolor: '#ede9fe',
              color: '#6d28d9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ClipboardList size={22} />
          </Box>
          <Box>
            <Typography variant='h6' fontWeight={700} color='text.primary'>
              CCA Question Bank
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Cognitive Capability Assessment — 5 subdomains × 10 levels × 50
              questions per category
            </Typography>
          </Box>
        </Stack>

        <Stack direction='row' spacing={1} alignItems='center' flexWrap='wrap'>
          {CCA_SUBDOMAINS.map((sd) => {
            const styles = subdomainColorMap[sd];
            return (
              <Chip
                key={sd}
                label={sd.split(' ')[0]}
                size='small'
                sx={{
                  bgcolor: styles?.bg,
                  color: styles?.color,
                  fontWeight: 600,
                  fontSize: '0.6875rem',
                }}
              />
            );
          })}
          <Button
            variant='contained'
            startIcon={<Plus size={16} />}
            onClick={() => {
              setEditingQuestion(null);
              setIsCreateOpen(true);
            }}
            sx={{
              bgcolor: '#10b981',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#059669',
                boxShadow: 'none',
              },
            }}
          >
            Add Question
          </Button>
        </Stack>
      </Box>

      {/* Stats bar */}
      <Paper
        variant='outlined'
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 2.5,
          bgcolor: 'grey.50',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          flexWrap: 'wrap',
        }}
      >
        {[
          {
            value: isLoading ? '—' : total.toLocaleString(),
            label: 'Total Questions',
          },
          { value: '5', label: 'Subdomains' },
          { value: '10', label: 'Levels' },
          { value: '50', label: 'Per Category/Level' },
        ].map((s, i, arr) => (
          <React.Fragment key={s.label}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant='subtitle2' fontWeight={700} color='text.primary'>
                {s.value}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {s.label}
              </Typography>
            </Box>
            {i < arr.length - 1 && (
              <Divider orientation='vertical' flexItem sx={{ my: 0.5 }} />
            )}
          </React.Fragment>
        ))}
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Filter size={16} color='#64748b' />
          <Typography variant='caption' color='text.secondary'>
            <strong>{visibleQuestions.length}</strong> showing
          </Typography>
        </Box>
      </Paper>

      {/* Filters */}
      <Paper
        variant='outlined'
        sx={{
          mb: 2,
          p: 1.5,
          borderRadius: 2,
          borderColor: 'divider',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          flexWrap='wrap'
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <TextField
              size='small'
              fullWidth
              placeholder='Search questions...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search size={16} color='#94a3b8' />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Select
            size='small'
            value={selectedSubdomain}
            onChange={(e) => {
              setSelectedSubdomain(e.target.value as CCASubdomain | 'All');
              handleFilterChange();
            }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value='All'>All Subdomains</MenuItem>
            {CCA_SUBDOMAINS.map((sd) => (
              <MenuItem key={sd} value={sd}>
                {sd}
              </MenuItem>
            ))}
          </Select>

          <Select
            size='small'
            value={selectedLevel}
            onChange={(e) => {
              setSelectedLevel(
                e.target.value === 'All' ? 'All' : Number(e.target.value),
              );
              handleFilterChange();
            }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value='All'>All Levels</MenuItem>
            {Array.from({ length: 10 }, (_, i) => i + 1).map((l) => (
              <MenuItem key={l} value={l}>
                Level {l} — {CCA_LEVEL_LABELS[l]}
              </MenuItem>
            ))}
          </Select>

          <Select
            size='small'
            value={selectedType}
            onChange={(e) => {
              setSelectedType(e.target.value);
              handleFilterChange();
            }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value='All'>All Types</MenuItem>
            <MenuItem value='multiple-choice'>Multiple Choice</MenuItem>
            <MenuItem value='short-answer'>Short Answer</MenuItem>
          </Select>

          {hasFilters && (
            <Button
              variant='outlined'
              size='small'
              onClick={() => {
                setSearchText('');
                setSelectedSubdomain('All');
                setSelectedLevel('All');
                setSelectedType('All');
                setPage(1);
              }}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                borderColor: 'divider',
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Loading / Error */}
      {isLoading && (
        <Stack spacing={1}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton
              key={i}
              variant='rectangular'
              height={48}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      )}

      {isError && (
        <Box
          sx={{
            py: 8,
            textAlign: 'center',
            color: 'error.main',
          }}
        >
          <ClipboardList size={32} />
          <Typography variant='body2' sx={{ mt: 1 }}>
            Failed to load questions. Please try again.
          </Typography>
        </Box>
      )}

      {/* Question list */}
      {!isLoading && !isError && (
        <>
          {visibleQuestions.length === 0 ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              <ClipboardList size={32} />
              <Typography variant='body2' sx={{ mt: 1 }}>
                No questions match your filters
              </Typography>
            </Box>
          ) : (
            <Box>
              {visibleQuestions.map((q) => (
                <CCAQuestionRow
                  key={q.id}
                  question={q}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </Box>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Paper
              variant='outlined'
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant='caption' color='text.secondary'>
                Page {page} of {totalPages} ({total.toLocaleString()} total)
              </Typography>
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <Button
                  size='small'
                  variant='outlined'
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ minWidth: 32, px: 1 }}
                >
                  Prev
                </Button>
                <Typography variant='caption' fontWeight={600} sx={{ px: 1 }}>
                  {page}
                </Typography>
                <Button
                  size='small'
                  variant='outlined'
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  sx={{ minWidth: 32, px: 1 }}
                >
                  Next
                </Button>
              </Stack>
            </Paper>
          )}
        </>
      )}

      <CreateQuestionDialog
        isOpen={isCreateOpen}
        onClose={handleDialogClose}
        question={editingQuestion ?? undefined}
      />

      {deletingQuestion && (
        <DeleteConfirmDialog
          question={deletingQuestion}
          isDeleting={isDeleting}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeletingQuestion(null)}
        />
      )}
    </Box>
  );
};

export default CCABank;
